import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import { ApolloError } from 'apollo-client';
import { flatten } from 'lodash';

import BoxBody from 'components/BoxBody';
import CoachingConstants, { CoachingSessionDuration } from 'coaching/CoachingConstants';
import CurrentUser from 'auth/CurrentUser';
import EventTracking from 'lib/EventTracking';
import LoadingIndicator from 'components/LoadingIndicator';
import NoCoachAvailabilityNullState from 'coaching/components/CoachingHomeView/NoCoachAvailabilityNullState';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import {
  DaySelectRow,
  TimeSelectRow,
  DurationSelectRow,
  SlotSelectRow,
  SlotSelect,
} from 'coaching/components/CoachingHomeView/BookSessionSelectRows';
import { getCoachAvailabilityForEvent } from 'coaching/actions/CoachingActions';
import {
  formatRecurrenceRules,
  mapRecurringFrequencyToInterval,
  RecurringFrequencyInput,
} from 'lib/dateTimeFormat';
import {
  groupSlotsByWeekdayAndStartTime,
  RecurringCoachingSessionSlotGroupings,
} from 'coaching/lib/coachingSessionSlots';
import {
  CoachingSessionSlot,
  CoachingTimeOff,
} from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { DayOfWeek, CoachingEventDefinitionInput } from 'globalTypes';
import { SelfScheduleOptions } from 'coaching/components/CoachingHomeView/BookNewCoachingSessionSidebar';

export type Props = {
  participantUuid: string;
  coachUuid: string;
  frequency: RecurringFrequencyInput;
  timezone: string;
  sessionSlots?: CoachingSessionSlot[];
  timeOff?: CoachingTimeOff[];
  onSelectReservation: (availableSchedule?: {
    eventDefinition: CoachingEventDefinitionInput;
    selectedAvailability: CoachingSessionSlot;
  }) => void;
  onLoadEventDefinition: (loading: boolean) => void;
  eventPrefix?: string;
  loading: boolean;
  error?: ApolloError;
  selfScheduleOptions: SelfScheduleOptions;
};

export default function BookRecurringSession(props: Props) {
  const {
    participantUuid,
    coachUuid,
    sessionSlots,
    frequency,
    timezone,
    onSelectReservation,
    onLoadEventDefinition,
    eventPrefix,
    loading,
    error,
    selfScheduleOptions,
  } = props;
  function trackKey(key: string, properties?: Record<string, any>) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`, properties);
  }

  const currentUser = CurrentUser.getUser();
  const hasMultipleRecurringSessionsFeature = Boolean(
    currentUser && currentUser.hasFeature('add_recurring_sessions.web.dec-21')
  );

  const [selectedDay, setSelectedDay] = useState<DayOfWeek>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [selectedDuration, setSelectedDuration] = useState<CoachingSessionDuration>(
    selfScheduleOptions.durationOptions[0]
  );
  const [selectedSlot, setSelectedSlot] = useState<CoachingSessionSlot>();

  useEffect(() => {
    // select the first day that has timeslots
    const firstDayWithSlots =
      groupedSlots &&
      CoachingConstants.VALID_COACHING_DAYS.find(day => !dayHasNoSlots(day, groupedSlots));
    setSelectedDay(firstDayWithSlots);
  }, [loading]);

  useEffect(() => {
    // reset times when the day is changed
    setSelectedTime(undefined);
  }, [selectedDay]);

  useEffect(() => {
    // reset selected slot when the time is changed
    setSelectedSlot(undefined);
    onSelectReservation(undefined);
  }, [selectedTime]);

  useEffect(() => {
    // if the slot just got unselected, no need to do anything else
    if (!selectedSlot) {
      return;
    }

    // otherwise, check for unavailabilities and call the handler with the
    // newly selected slot
    const proposedEventDefinition = generateEventDefinition(
      frequency,
      selectedDuration,
      selectedSlot,
      timezone
    );
    if (proposedEventDefinition) {
      onLoadEventDefinition(true);
      getCoachAvailabilityForEvent(coachUuid, proposedEventDefinition, participantUuid)
        .then(res => {
          const fullEventDefinition =
            res &&
            generateEventDefinition(
              frequency,
              selectedDuration,
              selectedSlot,
              timezone,
              res.coaching?.getCoachAvailabilityForEvent.unavailableDates
            );
          if (fullEventDefinition) {
            onSelectReservation({
              selectedAvailability: selectedSlot,
              eventDefinition: fullEventDefinition,
            });
          }
          onLoadEventDefinition(false);
        })
        .catch(e => {
          console.error('BookRecurringSession - failed to get availability for coach', {
            e,
            coachUuid,
          });
          onLoadEventDefinition(false);
        });
    }
  }, [selectedDuration, selectedSlot, timezone]);

  if (loading) {
    return (
      <BoxBody size="small">
        <Text align="center">
          <LoadingIndicator data-test="loading-indicator" />
        </Text>
      </BoxBody>
    );
  }
  if (error || !sessionSlots || sessionSlots.length === 0) {
    return <NoCoachAvailabilityNullState />;
  }

  const groupedSlots = groupSlotsByWeekdayAndStartTime(sessionSlots);
  return (
    <BoxBody size="small">
      <DaySelectRow
        groupedSlots={groupedSlots}
        selectedDay={selectedDay}
        onSelectDay={day => {
          trackKey('day_of_week.click', { data: day.toString() });
          setSelectedDay(day);
        }}
      />
      {selectedDay && (
        <TimeSelectRow
          slotsForSelectedDay={groupedSlots[selectedDay]}
          selectedTime={selectedTime}
          onSelectTime={time => {
            trackKey('start_time.click', { data: time });
            setSelectedTime(time);
          }}
        />
      )}
      {selfScheduleOptions.durationOptions.length > 1 && (
        <DurationSelectRow
          durationOptions={selfScheduleOptions.durationOptions}
          selectedDuration={selectedDuration}
          onSelectDuration={duration => {
            trackKey('length.click');
            setSelectedDuration(duration);
          }}
        />
      )}
      {hasMultipleRecurringSessionsFeature ? (
        <SlotSelect
          disabled={!(selectedDay && selectedTime)}
          placeholder="Select a start date"
          slotsForSelectedTime={
            selectedDay && selectedTime ? groupedSlots[selectedDay][selectedTime] : undefined
          }
          onSelectSlot={(isoStartDateTime: string) => {
            const slot = groupedSlots[selectedDay!][selectedTime!].find(
              slot => slot.startDateTime.toISOString() === isoStartDateTime
            );
            trackKey('start_date.click', { data: slot!.startDateTime.toISOString() });
            setSelectedSlot(slot!);
          }}
          selectedSlot={selectedSlot}
        />
      ) : (
        selectedDay &&
        selectedTime && (
          <SlotSelectRow
            slotsForSelectedTime={groupedSlots[selectedDay][selectedTime]}
            selectedSlot={selectedSlot}
            onSelectSlot={slot => {
              trackKey('start_date.click', { data: slot.startDateTime.toISOString() });
              setSelectedSlot(slot);
            }}
          />
        )
      )}
    </BoxBody>
  );
}

export function dayHasNoSlots(day: DayOfWeek, groupedSlots: RecurringCoachingSessionSlotGroupings) {
  return flatten(Object.values(groupedSlots[day])).length === 0;
}

function generateEventDefinition(
  frequency: RecurringFrequencyInput,
  duration: CoachingSessionDuration,
  slot: CoachingSessionSlot,
  timezone: string,
  exceptionDates?: (Date | string)[]
) {
  const startDateTime = slot.startDateTime
    .clone()
    .utc()
    .toISOString();
  const recurrenceInterval = mapRecurringFrequencyToInterval(frequency);
  if (!recurrenceInterval) {
    ToastActions.showErrorToast(
      'Oops! It looks like something went wrong. Please try selecting a different frequency.'
    );
    return undefined;
  }

  return {
    startDateTime,
    endDateTime: moment
      .utc(startDateTime)
      .minutes(duration)
      .toISOString(),
    recurrence: formatRecurrenceRules({
      interval: recurrenceInterval,
      excludeUnavailableDates: true,
      exceptionDates,
    }),
    timezone,
  };
}
